/**
 * Feedback API client — submit, list, update tickets via relay
 */

import type { FeedbackTicket, FeedbackType, FeedbackStatus } from '@galactus/shared';

// ── Relay URL resolution (same as clientLog.ts) ─────────────────────
function getRelayBaseUrl(): string {
  const env = (import.meta.env.VITE_RELAY_URL as string | undefined)?.trim();
  if (env && env.length > 0) return env;
  if (import.meta.env.DEV) return 'http://localhost:8787';
  return `${window.location.protocol}//${window.location.host}`;
}

const BUILD_COMMIT = (import.meta.env.VITE_BUILD_COMMIT as string) || 'dev';

// ── Browser diagnostics (auto-captured on submit) ───────────────────
function captureBrowserInfo() {
  return {
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    url: window.location.href,
    build: BUILD_COMMIT,
  };
}

// ── Public: submit feedback (no auth) ───────────────────────────────
export interface SubmitFeedbackData {
  text: string;
  type: FeedbackType;
  attachments?: { name: string; data: string }[];
}

export async function submitFeedback(data: SubmitFeedbackData): Promise<{ id: string }> {
  const res = await fetch(`${getRelayBaseUrl()}/relay/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text: data.text,
      type: data.type,
      attachments: data.attachments,
      browser: captureBrowserInfo(),
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error((err as { error?: string }).error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Admin: list tickets ─────────────────────────────────────────────
export async function fetchTickets(
  adminSecret: string,
  status?: FeedbackStatus
): Promise<FeedbackTicket[]> {
  const url = new URL(`${getRelayBaseUrl()}/relay/feedback`);
  if (status) url.searchParams.set('status', status);
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${adminSecret}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ── Admin: update ticket ────────────────────────────────────────────
export async function updateTicket(
  adminSecret: string,
  id: string,
  updates: Partial<Pick<FeedbackTicket, 'status' | 'priority' | 'type' | 'devNotes' | 'resolution'>>
): Promise<FeedbackTicket> {
  const res = await fetch(`${getRelayBaseUrl()}/relay/feedback/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${adminSecret}`,
    },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ── Upload URL helper ───────────────────────────────────────────────
export function getUploadUrl(filename: string): string {
  return `${getRelayBaseUrl()}/relay/feedback/uploads/${filename}`;
}
